import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Tag,
  FolderTree,
  Menu as MenuIcon,
  MessageSquare,
  Settings,
  Send,
  Megaphone,
  LogOut,
  ChevronDown,
  Plus,
  Store,
  X,
  Key,
  Upload,
  FileText,
  HelpCircle,
  User,
  ChevronRight,
  FileImage,
  Database,
  AppWindow,
  Music,
  Film,
  QrCode
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Layout({ user, onLogout, selectedTeam, onTeamChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [teamFeatures, setTeamFeatures] = useState(null);
  const [featuresLoading, setFeaturesLoading] = useState(true);

  useEffect(() => {
    loadTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam && teams.length > 0) {
      const team = teams.find(t => t.id === selectedTeam);
      setCurrentTeam(team);
      loadTeamFeatures(selectedTeam);
    }
  }, [selectedTeam, teams]);

  const loadTeams = async () => {
    try {
      const res = await axiosInstance.get('/teams');
      setTeams(res.data);
      if (res.data.length > 0 && !selectedTeam) {
        onTeamChange(res.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load teams', err);
    }
  };

  const loadTeamFeatures = async (teamId) => {
    setFeaturesLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${teamId}/features`);
      setTeamFeatures(res.data.features);
    } catch (err) {
      console.error('Failed to load features:', err);
      // Default all features to true if fetch fails
      setTeamFeatures(null);
    } finally {
      setFeaturesLoading(false);
    }
  };

  const handleTeamSwitch = (teamId) => {
    onTeamChange(teamId);
    const team = teams.find(t => t.id === teamId);
    setCurrentTeam(team);
    setFeaturesLoading(true); // Show loading while switching
    toast.success(`${team.name} mağazasına geçildi`);
    navigate('/');
  };

  // Feature key mapping for menu items
  const featureMap = {
    '/product-upload': 'product_upload',
    '/video-audio': 'video_audio',
    '/video-match': 'video_match',
    '/data-match': 'data_match',
    '/file-rename': 'file_rename',
    '/announcement': 'announcements',
    '/shopify-settings': 'shopify',
    '/bot-settings': 'auto_menu', // Bot settings includes auto menu
  };

  // Check if a menu item should be visible
  const isFeatureEnabled = (path) => {
    const featureKey = featureMap[path];
    if (!featureKey) return true; // No feature control for this path (e.g., dashboard, settings)
    if (featuresLoading) return false; // Hide all controlled features while loading
    if (!teamFeatures) return true; // Show all if features fetch failed (fallback)
    return teamFeatures[featureKey] !== false; // Show unless explicitly disabled
  };

  const menuGroups = [
    {
      title: 'Ana Menü',
      items: [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
        { path: '/teams', icon: Users, label: 'Mağazalar', testId: 'nav-teams' },
      ]
    },
    {
      title: 'İçerik Yönetimi',
      items: [
        { path: '/menu-items', icon: MenuIcon, label: 'Menü Butonları', testId: 'nav-menu-items' },
        { path: '/brands', icon: Tag, label: 'Markalar', testId: 'nav-brands' },
        { path: '/categories', icon: FolderTree, label: 'Kategoriler', testId: 'nav-categories' },
        { path: '/messages', icon: MessageSquare, label: 'Mesajlar', testId: 'nav-messages' },
      ]
    },
    {
      title: 'Yayın İşlemleri',
      items: [
        { path: '/product-upload', icon: Upload, label: 'Ürün Yükleme', testId: 'nav-product-upload' },
        { path: '/send-menu', icon: Send, label: 'Menü Gönder', testId: 'nav-send-menu' },
        { path: '/announcement', icon: Megaphone, label: 'Duyuru Yap', testId: 'nav-announcement' },
      ]
    },
    {
      title: 'Uygulamalar',
      items: [
        { path: '/file-rename', icon: FileImage, label: 'İsim Değiştir', testId: 'nav-file-rename' },
        { path: '/data-match', icon: Database, label: 'Veri Eşleştir', testId: 'nav-data-match' },
        { path: '/video-audio', icon: Music, label: 'Video + Ses', testId: 'nav-video-audio' },
        { path: '/video-match', icon: Film, label: 'Video Eşleştir', testId: 'nav-video-match' },
        { path: '/qr-poster', icon: QrCode, label: 'QR Poster', testId: 'nav-qr-poster' },
      ]
    },
    {
      title: 'Ayarlar',
      items: [
        { path: '/bot-settings', icon: Settings, label: 'Bot Ayarları', testId: 'nav-bot-settings' },
        { path: '/caption-settings', icon: FileText, label: 'Ürün Etiketleri', testId: 'nav-caption-settings' },
        { path: '/shopify-settings', icon: Store, label: 'Shopify Entegrasyonu', testId: 'nav-shopify-settings' },
        { path: '/api-settings', icon: Key, label: 'API Ayarları', testId: 'nav-api-settings' },
        { path: '/qr-poster-settings', icon: QrCode, label: 'QR Poster Ayarları', testId: 'nav-qr-poster-settings' },
        { path: '/account-settings', icon: User, label: 'Hesap Ayarları', testId: 'nav-account-settings' },
      ]
    },
    {
      title: 'Yardım',
      items: [
        { path: '/help', icon: HelpCircle, label: 'Yardım & Destek', testId: 'nav-help' },
      ]
    }
  ];

  const handleMenuItemClick = () => {
    setMobileMenuOpen(false);
  };

  // Export features check function for child components
  const checkFeatureAccess = (path) => {
    return isFeatureEnabled(path);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-sm z-40 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Store className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Bot Panel</h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`w-72 bg-white border-r border-gray-200 shadow-lg flex flex-col fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="hidden lg:flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Telegram Bot</h1>
              <p className="text-xs text-gray-500">Yönetim Paneli</p>
            </div>
          </div>
          
          {/* Team Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-between hover:bg-purple-50 hover:border-purple-300 transition-all"
                data-testid="team-switcher"
              >
                <span className="truncate">{currentTeam ? currentTeam.name : 'Mağaza Seçin'}</span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              <DropdownMenuLabel>Mağazalarım</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {teams.map(team => (
                <DropdownMenuItem 
                  key={team.id} 
                  onClick={() => handleTeamSwitch(team.id)}
                  data-testid={`team-option-${team.name}`}
                >
                  <Store className="w-4 h-4 mr-2" />
                  {team.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/teams')} data-testid="create-new-team">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Mağaza Oluştur
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 p-2 sm:p-4 space-y-4 overflow-y-auto">
          {menuGroups.map((group, groupIdx) => (
            <div key={groupIdx}>
              <h3 className="px-3 sm:px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.filter(item => isFeatureEnabled(item.path)).map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleMenuItemClick}
                      data-testid={item.testId}
                      className={`flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30'
                          : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium text-sm sm:text-base">{item.label}</span>
                      </div>
                      {isActive && <ChevronRight className="w-4 h-4" />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-lg">
                {user?.username?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{user?.username}</p>
                <p className="text-xs text-gray-600 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="hover:bg-red-100 hover:text-red-600 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0">
        <Outlet context={{ teamFeatures, featuresLoading, isFeatureEnabled }} />
      </main>
    </div>
  );
}
